import React, { useEffect, useMemo, useState } from "react";
import SEO from "@/components/SEO";
import { toast } from "sonner";
import { Search, Download, Check, X, AlertTriangle, ShieldCheck, Calendar, ChevronRight, Image as ImageIcon } from "lucide-react";
import { adminApi, formatErr } from "@/admin/api";
import { Panel, AdminSelect, EmptyState } from "@/admin/components/ui";

const fmtDate = (iso) => { try { return new Date(iso).toLocaleString("en-ZA", { dateStyle: "medium", timeStyle: "short" }); } catch { return iso; } };

const STATUS_LABEL = {
  success: { label: "Success", tone: "text-[#7fe6ff] border-[#7fe6ff]/40", icon: Check },
  denied: { label: "Denied", tone: "text-[#ff9fcf] border-[#ff9fcf]/40", icon: X },
  failed: { label: "Failed", tone: "text-[#ff9fcf] border-[#ff9fcf]/40", icon: AlertTriangle },
  missing_original: { label: "Missing Original", tone: "text-[#ffd479] border-[#ffd479]/40", icon: AlertTriangle },
  expired: { label: "Expired", tone: "text-white/55 border-white/15", icon: AlertTriangle },
};

const StatusPill = ({ status }) => {
  const o = STATUS_LABEL[status] || { label: status || "—", tone: "text-white/55 border-white/15", icon: AlertTriangle };
  const Icon = o.icon;
  return (
    <span data-testid={`downloads-status-${status}`} className={`inline-flex items-center gap-1 text-[10px] tracking-luxe uppercase px-2 py-0.5 rounded-full glass-strong border ${o.tone}`}>
      <Icon className="w-3 h-3" /> {o.label}
    </span>
  );
};

const DetailDrawer = ({ id, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let active = true;
    adminApi.get(`/admin/downloads/${id}`)
      .then((r) => { if (active) setData(r.data); })
      .catch((err) => toast.error(formatErr(err)))
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [id]);
  return (
    <>
      <div className="fixed inset-0 bg-black/75 backdrop-blur-sm z-50" onClick={onClose} data-testid="downloads-detail-overlay" />
      <aside data-testid="downloads-detail-drawer" className="fixed top-0 right-0 bottom-0 w-full sm:w-[520px] z-50 glass-strong border-l border-white/10 overflow-y-auto">
        <header className="px-5 py-4 border-b border-white/5 flex items-center gap-3 sticky top-0 glass-strong">
          <Download className="w-4 h-4 text-[#ff2e93]" />
          <h2 className="font-display text-lg text-chrome flex-1">Download Record</h2>
          <button onClick={onClose} data-testid="downloads-detail-close" className="w-8 h-8 rounded-full border border-white/10 text-white/70 hover:text-white flex items-center justify-center"><X className="w-4 h-4" /></button>
        </header>
        <div className="px-5 py-5 space-y-4">
          {loading || !data ? <p className="text-sm text-white/40">Loading…</p> : (
            <>
              <Panel>
                <p className="text-[10px] tracking-luxe uppercase text-white/45">Customer</p>
                <p className="text-sm text-white">{data.download.customer_email || data.download.customer_id || "—"}</p>
              </Panel>
              <Panel>
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div><p className="text-[10px] tracking-luxe uppercase text-white/45">Status</p><StatusPill status={data.download.status} /></div>
                  <div><p className="text-[10px] tracking-luxe uppercase text-white/45">Method</p><p className="text-white">{data.download.method || "—"}</p></div>
                  <div><p className="text-[10px] tracking-luxe uppercase text-white/45">When</p><p className="text-white">{fmtDate(data.download.created_at)}</p></div>
                  <div><p className="text-[10px] tracking-luxe uppercase text-white/45">IP</p><p className="text-white">{data.download.ip_address || "—"}</p></div>
                  <div className="col-span-2"><p className="text-[10px] tracking-luxe uppercase text-white/45">User-Agent</p><p className="text-xs text-white/65 break-words">{data.download.user_agent || "—"}</p></div>
                  {data.download.failure_reason && <div className="col-span-2"><p className="text-[10px] tracking-luxe uppercase text-[#ff9fcf]">Failure reason</p><p className="text-xs text-[#ff9fcf]">{data.download.failure_reason}</p></div>}
                </div>
              </Panel>
              {data.purchased_photo && (
                <Panel title="Purchased Photo">
                  <div className="text-xs space-y-1">
                    <p className="text-white">{data.purchased_photo.display_name}</p>
                    <p className="text-white/55">Downloads: {data.purchased_photo.download_count || 0}</p>
                    <p className="text-white/55">Last: {data.purchased_photo.last_downloaded_at ? fmtDate(data.purchased_photo.last_downloaded_at) : "—"}</p>
                    <p className="text-white/55">Status: {data.purchased_photo.download_status}</p>
                  </div>
                </Panel>
              )}
              <div className="text-[10px] tracking-luxe uppercase text-white/40 inline-flex items-center gap-2">
                <ShieldCheck className="w-3 h-3 text-[#ff2e93]" />
                Signed URLs are never persisted — only the attempt is logged.
              </div>
            </>
          )}
        </div>
      </aside>
    </>
  );
};

export default function AdminDownloads() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [statusF, setStatusF] = useState("all");
  const [active, setActive] = useState(null);

  useEffect(() => {
    setLoading(true);
    adminApi.get("/admin/downloads")
      .then((r) => setRows(r.data))
      .catch((err) => toast.error(formatErr(err)))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => rows.filter((r) => {
    if (statusF !== "all" && r.status !== statusF) return false;
    if (!q) return true;
    const blob = `${r.customer_email} ${r.event_title} ${r.display_name} ${r.purchased_photo_id}`.toLowerCase();
    return blob.includes(q.toLowerCase());
  }), [rows, q, statusF]);

  const totals = useMemo(() => {
    return {
      total: filtered.length,
      success: filtered.filter((r) => r.status === "success").length,
      denied: filtered.filter((r) => r.status === "denied").length,
      missing: filtered.filter((r) => r.status === "missing_original").length,
    };
  }, [filtered]);

  return (
    <div data-testid="page-admin-downloads" className="space-y-6">
      <SEO title="AdminDownloads · WelkomBiz Photography" noindex />
      <Panel testId="downloads-explainer">
        <div className="flex items-start gap-3">
          <ShieldCheck className="w-4 h-4 text-[#ff2e93] mt-0.5 shrink-0" />
          <p className="text-sm text-white/65 font-body">
            Every download attempt — successful, denied, missing-original or failed — is logged here. Signed download URLs are
            <strong> never</strong> persisted to the database. Only the attempt context (customer, photo, IP, status) is recorded.
          </p>
        </div>
      </Panel>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="flex items-center gap-3 flex-1 glass chrome-border rounded-full px-4 py-2">
          <Search className="w-4 h-4 text-white/45" />
          <input data-testid="downloads-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by customer, event, photo…" className="flex-1 bg-transparent text-sm text-white placeholder-white/35 outline-none font-body" />
        </div>
        <AdminSelect data-testid="downloads-filter-status" value={statusF} onChange={(e) => setStatusF(e.target.value)} className="sm:w-48">
          <option value="all" className="bg-black">All statuses</option>
          <option value="success" className="bg-black">Success</option>
          <option value="denied" className="bg-black">Denied</option>
          <option value="missing_original" className="bg-black">Missing Original</option>
          <option value="failed" className="bg-black">Failed</option>
        </AdminSelect>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div data-testid="downloads-total" className="glass chrome-border rounded-md p-3"><p className="text-[10px] tracking-luxe uppercase text-white/45">Total Attempts</p><p className="font-display text-2xl text-chrome">{totals.total}</p></div>
        <div data-testid="downloads-success-count" className="glass chrome-border rounded-md p-3"><p className="text-[10px] tracking-luxe uppercase text-white/45">Success</p><p className="font-display text-2xl text-[#7fe6ff]">{totals.success}</p></div>
        <div data-testid="downloads-denied-count" className="glass chrome-border rounded-md p-3"><p className="text-[10px] tracking-luxe uppercase text-white/45">Denied</p><p className="font-display text-2xl text-[#ff9fcf]">{totals.denied}</p></div>
        <div data-testid="downloads-missing-count" className="glass chrome-border rounded-md p-3"><p className="text-[10px] tracking-luxe uppercase text-white/45">Missing</p><p className="font-display text-2xl text-[#ffd479]">{totals.missing}</p></div>
      </div>

      <Panel className="!p-0 overflow-hidden" testId="downloads-table">
        {loading ? <p className="p-6 text-sm text-white/40">Loading…</p> :
         filtered.length === 0 ? <div className="p-6"><EmptyState title="No download attempts" message="Records will appear here as customers download their purchased originals." /></div> :
         (<>
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-white/3 text-[10px] tracking-luxe uppercase text-white/45">
                <tr>
                  <th className="text-left px-5 py-3">When</th>
                  <th className="text-left px-5 py-3">Customer</th>
                  <th className="text-left px-5 py-3">Photo</th>
                  <th className="text-left px-5 py-3">Event</th>
                  <th className="text-left px-5 py-3">Status</th>
                  <th className="text-left px-5 py-3">IP</th>
                  <th className="text-right px-5 py-3">Action</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr key={r.id} data-testid={`downloads-row-${r.id}`} className="border-t border-white/5 hover:bg-white/3">
                    <td className="px-5 py-3 text-white/70 whitespace-nowrap"><Calendar className="w-3 h-3 inline mr-1 text-white/40" />{fmtDate(r.created_at)}</td>
                    <td className="px-5 py-3 text-white/85 truncate max-w-[200px]">{r.customer_email || "—"}</td>
                    <td className="px-5 py-3 text-white truncate max-w-[200px]">{r.display_name || r.purchased_photo_id?.slice(0,8) || "—"}</td>
                    <td className="px-5 py-3 text-white/70 truncate max-w-[180px]">{r.event_title || "—"}</td>
                    <td className="px-5 py-3"><StatusPill status={r.status} /></td>
                    <td className="px-5 py-3 text-white/45 whitespace-nowrap">{r.ip_address || "—"}</td>
                    <td className="px-5 py-3 text-right">
                      <button onClick={() => setActive(r.id)} data-testid={`downloads-view-${r.id}`} className="text-[10px] tracking-luxe uppercase text-white/70 hover:text-white inline-flex items-center gap-1">
                        View <ChevronRight className="w-3 h-3" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="md:hidden divide-y divide-white/5">
            {filtered.map((r) => (
              <button key={r.id} data-testid={`downloads-card-${r.id}`} onClick={() => setActive(r.id)} className="block w-full text-left p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm text-white truncate">{r.customer_email || "—"}</p>
                    <p className="text-[10px] tracking-luxe uppercase text-white/45 mt-0.5 truncate">{r.event_title || r.display_name || "—"}</p>
                    <p className="text-[10px] tracking-luxe uppercase text-white/40 mt-0.5">{fmtDate(r.created_at)}</p>
                  </div>
                  <StatusPill status={r.status} />
                </div>
              </button>
            ))}
          </div>
         </>)}
      </Panel>

      {active && <DetailDrawer id={active} onClose={() => setActive(null)} />}
    </div>
  );
}
